import React, { useEffect, useState } from "react";
import { Copy, Send, UserCheck, ClipboardList, BellRing, Ban } from "lucide-react";
import { toast } from "sonner";
import { Card, Pill, PrimaryButton } from "./UI";
import api from "../lib/api";

const errText = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Request failed");

export const ClientPortalPanel = ({ client }) => {
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState("");
  const [forms, setForms] = useState([]);
  const [prefs, setPrefs] = useState({ reminder_email: client.reminder_email ?? true, reminder_sms: client.reminder_sms ?? false });
  const load = () => {
    api.get(`/portal/invite/${client.id}/status`).then(({ data }) => setStatus(data));
    api.get("/portal/forms/assigned", { params: { client_id: client.id } }).then(({ data }) => setForms(data));
  };
  useEffect(load, [client.id]);

  const invite = async () => {
    try {
      const { data } = await api.post(`/portal/invite/${client.id}`, { origin_url: window.location.origin });
      setLink(data.invite_link);
      toast.success(data.email_sent ? `Invitation emailed to ${data.email}` : "Invite created — email couldn't be sent, copy the link instead");
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const revoke = async () => { await api.post(`/portal/revoke/${client.id}`); toast.success("Portal access disabled"); load(); };
  const assign = async (form_type) => {
    try { await api.post("/portal/forms/assign", { client_id: client.id, form_type }); toast.success("Form sent to client"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const savePrefs = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    const prev = prefs;
    setPrefs(next);
    try {
      await api.put(`/clients/${client.id}/reminders`, next);
      toast.success(next.reminder_sms && key === "reminder_sms" ? "Text reminders on — client consent recorded as attested by you" : "Reminder preferences saved");
    } catch (e) { setPrefs(prev); toast.error(errText(e)); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="client-portal-panel">
      <Card testid="portal-access-card">
        <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><UserCheck className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> Portal access</h3>
        {!status ? <div className="mf-skeleton h-16" /> : (
          <>
            <div className="flex items-center gap-2 mb-4 text-sm">
              <Pill variant={status.activated && status.active ? "success" : status.invited ? "warn" : "default"}>
                <span data-testid="portal-access-status">{!status.invited ? "Not invited" : !status.active ? "Disabled" : status.activated ? "Active" : "Invite pending"}</span>
              </Pill>
              {status.last_login && <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Last sign-in {new Date(status.last_login).toLocaleDateString()}</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              <PrimaryButton onClick={invite} testid="portal-invite-btn"><span className="flex items-center gap-2"><Send className="w-4 h-4" /> {status.activated ? "Send new setup link" : status.invited ? "Resend invite" : "Invite to portal"}</span></PrimaryButton>
              {status.invited && status.active && <button onClick={revoke} className="text-xs px-3 py-2 rounded-full flex items-center gap-1" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c" }} data-testid="portal-revoke-btn"><Ban className="w-3 h-3" /> Disable</button>}
            </div>
            {!client.email && <p className="text-xs mt-3" style={{ color: "#b91c1c" }}>Add an email address to this client to invite them.</p>}
            {link && (
              <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Invite link copied"); }} className="mt-3 text-xs flex items-center gap-1 break-all text-left" style={{ color: "var(--mf-primary)" }} data-testid="portal-copy-link">
                <Copy className="w-3 h-3 shrink-0" /> Copy invite link (valid 72h)
              </button>
            )}
          </>
        )}
        <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--mf-muted)" }}>
          <div className="text-sm font-medium mb-2 flex items-center gap-2"><BellRing className="w-4 h-4" style={{ color: "var(--mf-accent)" }} /> Session reminders</div>
          {[["reminder_email", "Email reminders (24h & 2h before)"], ["reminder_sms", "Text reminders (needs mobile number + client consent)"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
              <input type="checkbox" checked={prefs[k]} onChange={() => savePrefs(k)} data-testid={`client-${k}`} /> {label}
            </label>
          ))}
        </div>
      </Card>
      <Card testid="portal-forms-card">
        <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><ClipboardList className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> Forms for client</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {[["intake", "New Client Intake"], ["phq9", "PHQ-9"], ["gad7", "GAD-7"]].map(([k, label]) => (
            <button key={k} onClick={() => assign(k)} disabled={!status?.activated || !status?.active} className="text-xs px-3 py-2 rounded-full disabled:opacity-40" style={{ background: "var(--mf-muted)", color: "var(--mf-primary)" }} data-testid={`assign-form-${k}`}>Send {label}</button>
          ))}
        </div>
        {!status?.activated && <p className="text-xs mb-3" style={{ color: "var(--mf-text-muted)" }}>Forms can be sent once the client has activated their portal.</p>}
        <div className="space-y-2">
          {forms.map((f) => (
            <div key={f.id} className="p-3 rounded-xl text-sm" style={{ background: "var(--mf-muted)" }} data-testid={`assigned-form-${f.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{f.title}</span>
                <Pill variant={f.status === "completed" ? "success" : "warn"}>{f.status === "completed" ? (f.score != null ? `Score ${f.score}` : "Completed") : "Pending"}</Pill>
              </div>
              {f.form_type === "intake" && f.answers && (
                <div className="mt-2 space-y-1 text-xs" style={{ color: "var(--mf-text-muted)" }}>
                  {Object.entries(f.answers).map(([k, v]) => <div key={k}><b className="capitalize">{k.replace(/_/g, " ")}:</b> {String(v)}</div>)}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
